<script setup>
import { useShowProfileData } from "./business_logic";
import {
  fieldRequired,
  validateIfNotEmpty,
  emailValid,
  minLength,
} from "@/utils/input_validation.js";
import { examplePhoneNumber } from "@/utils/constants";
import {
  getYearsAgoAsYMD,
  addPostalCodeToPostalTown,
  stringNotEmpty,
} from "@/utils/functions";
import PhoneNumberInput from "@/components/inputs/PhoneNumberInput.vue";
import DatePicker from "@/components/inputs/DatePicker.vue";
import Labelledfield from "@/components/utils/LabelledField";
import { computed, ref } from "vue";
import LoadingArea from "@/components/utils/LoadingArea.vue";
import {
  countyIsKirinyaga,
  userDataLoading,
  userProfile,
} from "@/utils/vue_helpers";
import { getDisplayRoleName } from "@/utils/permissions";
import ProfessionalDetailsSection from "./ProfessionalDetailsSection.vue";
import { fetchUserDataOnMount } from "@/utils/vue_helpers";

defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
});

fetchUserDataOnMount();

const {
  userProfileModels,
  supportingFormData,
  nameLabel,
  isIndividual,
  idOrBusinessText,
  updateUser,
  usingDefaultDateOfBirth,
  updateUserMutatation,
  errorItems,
} = useShowProfileData();

const formRef = ref(null);

function onSubmitForm() {
  const formValid = formRef.value?.validate();
  if (formValid) {
    updateUser();
  }
}

const emailRules = computed(() => {
  const alreadyHasEmailAddress = stringNotEmpty(
    userProfile.value.email_address
  );
  const fieldIsRequired = !countyIsKirinyaga.value || alreadyHasEmailAddress;
  return [
    fieldIsRequired ? fieldRequired : () => true,
    validateIfNotEmpty([emailValid]),
  ];
});

const dateOfBirthLabel = computed(() => {
  if (isIndividual.value) {
    return "Date of Birth";
  }
  return "Date of Registration";
});

const displayRole = computed(() =>
  getDisplayRoleName(userProfileModels.role_name)
);

const customerInitials = computed(() => {
  const name = userProfileModels.customerName || "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || "?";
});
</script>

<template>
  <v-container fluid class="px-2 px-sm-3 py-3 py-md-4">
    <v-row justify="center" class="mb-2">
      <v-col cols="12" class="text-h6 text-md-h5 font-weight-bold primary--text text-center text-md-left">
        My Profile
      </v-col>
    </v-row>

    <!-- Profile summary hero (mobile-first) -->
    <v-card rounded="lg" outlined class="mb-4 pa-4">
      <div class="d-flex align-center flex-nowrap">
        <v-avatar color="primary" size="56" class="flex-shrink-0 white--text text-h6">
          {{ customerInitials }}
        </v-avatar>
        <div class="ml-4 overflow-hidden">
          <div class="text-subtitle-1 text-md-h6 font-weight-bold text-truncate">
            {{ userProfileModels.customerName || "—" }}
          </div>
          <div class="text-caption grey--text text--darken-1 text-truncate">
            {{ idOrBusinessText }}: {{ userProfileModels.idNumber }}
          </div>
          <v-chip
            v-if="userProfileModels.role_name"
            x-small
            color="primary"
            outlined
            class="mt-1 font-weight-medium"
          >
            {{ displayRole }}
          </v-chip>
        </div>
      </div>
    </v-card>

    <ProfessionalDetailsSection />

    <LoadingArea
      :loading="updateUserMutatation.isLoading.value || userDataLoading"
    >
      <v-form ref="formRef" @submit.prevent="onSubmitForm">
        <!-- Identity -->
        <v-card rounded="lg" outlined class="mb-4">
          <v-card-subtitle class="font-weight-bold primary--text pb-0 pt-3">
            <v-icon small left>mdi-card-account-details</v-icon>
            Identity
          </v-card-subtitle>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <Labelledfield :label="`${idOrBusinessText} Number`">
                  <v-text-field
                    outlined
                    dense
                    v-model="userProfileModels.idNumber"
                    hide-details="auto"
                    class="text-field-background"
                    disabled
                  />
                </Labelledfield>
              </v-col>
              <v-col cols="12" md="6">
                <Labelledfield label="Customer Type">
                  <v-select
                    :items="supportingFormData.customerTypes"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    v-model="userProfileModels.customerTypeId"
                    hide-details="auto"
                    disabled
                    class="text-field-background"
                  />
                </Labelledfield>
              </v-col>
              <v-col cols="12">
                <Labelledfield :label="nameLabel">
                  <v-text-field
                    outlined
                    dense
                    v-model="userProfileModels.customerName"
                    hide-details="auto"
                    class="text-field-background"
                    disabled
                  />
                </Labelledfield>
              </v-col>
              <v-col v-if="isIndividual" cols="12" md="6">
                <Labelledfield label="Gender">
                  <v-select
                    :items="supportingFormData.genders"
                    :rules="[fieldRequired]"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    v-model="userProfileModels.genderId"
                    hide-details="auto"
                    class="text-field-background"
                  />
                </Labelledfield>
              </v-col>
              <v-col cols="12" md="6">
                <Labelledfield :label="dateOfBirthLabel">
                  <div class="d-flex align-center">
                    <DatePicker
                      :rules="[fieldRequired]"
                      :max="getYearsAgoAsYMD(18)"
                      :textFieldProps="{
                        outlined: true,
                        dense: true,
                        class: 'text-field-background flex-grow-1',
                        'hide-details': 'auto',
                      }"
                      v-model="userProfileModels.dateOfBirth"
                    />
                    <v-tooltip v-if="usingDefaultDateOfBirth" top>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          class="ml-2 flex-shrink-0"
                          color="secondary"
                          v-on="on"
                          v-bind="attrs"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>
                        Default date of birth — select a new date and tap Update to save.
                      </span>
                    </v-tooltip>
                  </div>
                </Labelledfield>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Contact -->
        <v-card rounded="lg" outlined class="mb-4">
          <v-card-subtitle class="font-weight-bold primary--text pb-0 pt-3">
            <v-icon small left>mdi-phone</v-icon>
            Contact
          </v-card-subtitle>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <Labelledfield
                  :label="`Phone Number 1 (e.g. ${examplePhoneNumber})`"
                  required
                >
                  <PhoneNumberInput
                    v-model="userProfileModels.phoneNumber1"
                    :countryCode.sync="userProfileModels.phoneNumber1CountryCode"
                    :rules="[fieldRequired, minLength(9)]"
                    :error-messages="errorItems.phone_number1.errorValue ?? []"
                  />
                </Labelledfield>
              </v-col>
              <v-col cols="12" md="6">
                <Labelledfield
                  :label="`Phone Number 2 (e.g. ${examplePhoneNumber})`"
                >
                  <PhoneNumberInput
                    v-model="userProfileModels.phoneNumber2"
                    :countryCode.sync="userProfileModels.phoneNumber2CountryCode"
                    :rules="[validateIfNotEmpty([minLength(9)])]"
                    :error-messages="errorItems.phone_number2.errorValue ?? []"
                  />
                </Labelledfield>
              </v-col>
              <v-col cols="12">
                <Labelledfield
                  label="Email Address"
                  :required="!countyIsKirinyaga"
                >
                  <v-text-field
                    outlined
                    dense
                    v-model="userProfileModels.emailAddress"
                    :rules="emailRules"
                    hide-details="auto"
                    class="text-field-background"
                    :error-messages="errorItems.email_address.errorValue ?? []"
                  />
                </Labelledfield>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Address -->
        <v-card rounded="lg" outlined class="mb-4">
          <v-card-subtitle class="font-weight-bold primary--text pb-0 pt-3">
            <v-icon small left>mdi-map-marker</v-icon>
            Postal address
          </v-card-subtitle>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <Labelledfield label="Postal Town">
                  <v-autocomplete
                    :items="supportingFormData.postalTowns"
                    item-value="postal_town"
                    :item-text="addPostalCodeToPostalTown"
                    cache-items
                    outlined
                    dense
                    hide-no-data
                    hide-details="auto"
                    v-model="userProfileModels.postalTown"
                    class="text-field-background"
                  />
                </Labelledfield>
              </v-col>
              <v-col cols="12" md="6">
                <Labelledfield label="Postal Address">
                  <v-text-field
                    outlined
                    dense
                    v-model="userProfileModels.postalAddress"
                    hide-details="auto"
                    class="text-field-background"
                  />
                </Labelledfield>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Disability notice -->
        <v-alert type="info" text dense rounded="lg" class="mb-4 text-body-2">
          <template #prepend>
            <v-icon small>mdi-information-outline</v-icon>
          </template>
          For persons with disability, visit your nearest sub-county revenue office with
          <abbr>NCPLWD</abbr> registration details for assistance.
        </v-alert>

        <!-- Save — sticky on mobile -->
        <div class="profile-save-bar">
          <v-btn
            block
            depressed
            large
            class="text-none"
            color="secondary"
            type="submit"
            :loading="updateUserMutatation.isLoading.value"
          >
            Save profile
            <v-icon right>mdi-content-save</v-icon>
          </v-btn>
        </div>
      </v-form>
    </LoadingArea>
  </v-container>
</template>

<style scoped>
.profile-save-bar {
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding: 12px 0 4px;
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.98) 70%,
    rgba(255, 255, 255, 0)
  );
}

@media (min-width: 960px) {
  .profile-save-bar {
    position: static;
    background: none;
    padding: 0;
  }

  .profile-save-bar .v-btn {
    max-width: 240px;
    margin-left: auto;
    display: flex;
  }
}
</style>
